import { useRef, useState } from "react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { UploadCloud, FileCheck2, Info } from "lucide-react";
import api from "../lib/api";

function Asterisk({ show }) {
  return show ? <span className="text-[#C44F4F]"> *</span> : null;
}

function FileField({ block, docs, onUploaded, readonly }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const existing = (docs || []).find((d) => d.field === block.key);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("field", block.key);
      fd.append("file", file);
      const { data } = await api.post("/application/me/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(data);
      toast.success("File uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <input ref={inputRef} type="file" className="hidden" onChange={handle}
        accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" data-testid={`file-input-${block.key}`} disabled={readonly} />
      <button type="button" disabled={readonly || busy} onClick={() => inputRef.current?.click()}
        data-testid={`upload-${block.key}`}
        className="flex w-full items-center gap-3 rounded-lg border-2 border-dashed border-[#E5E2DC] bg-[#F4F2EC] px-5 py-6 text-left transition-colors hover:border-[#2C5545] disabled:opacity-60">
        {existing ? <FileCheck2 className="h-6 w-6 text-[#3A7D5E]" /> : <UploadCloud className="h-6 w-6 text-[#515E58]" />}
        <div>
          <div className="text-sm font-medium text-[#1F2924]">
            {busy ? "Uploading…" : existing ? existing.original_filename : "Click to upload"}
          </div>
          <div className="text-xs text-[#515E58]">PDF, PNG, JPG or Word document</div>
        </div>
      </button>
    </div>
  );
}

export function InfoBlock({ block }) {
  const variantStyles = {
    scenario: "border-l-4 border-[#D56A47] bg-[#FBF2ED]",
    banner: "border border-[#2C5545]/20 bg-[#EAF0ED]",
    warning: "border-l-4 border-[#E89B31] bg-[#FDF6EA]",
    plan: "border border-[#E5E2DC] bg-white",
  };
  const cls = variantStyles[block.variant] || "border border-[#E5E2DC] bg-white";
  return (
    <div className={`rounded-lg p-6 ${cls}`}>
      {block.heading && (
        <h4 className="font-heading text-lg font-semibold text-[#1F2924] flex items-center gap-2">
          {block.variant === "banner" && <Info className="h-5 w-5 text-[#2C5545]" />}
          {block.heading}
        </h4>
      )}
      {block.paragraphs?.map((p, i) => (
        <p key={i} className="mt-3 text-[15px] leading-relaxed text-[#515E58]">{p}</p>
      ))}
      {block.bullets && (
        <ul className="mt-3 list-disc space-y-1 pl-5 text-[15px] leading-relaxed text-[#515E58]">
          {block.bullets.map((b, i) => <li key={i}>{b}</li>)}
        </ul>
      )}
      {block.goals && (
        <div className="mt-4 space-y-4">
          {block.goals.map((g, i) => (
            <div key={i} className="rounded-lg border border-[#E5E2DC] bg-[#FAF9F6] p-5">
              <div className="font-heading font-semibold text-[#2C5545]">{g.title}</div>
              <p className="mt-2 text-sm text-[#1F2924]">{g.body}</p>
              <p className="mt-2 text-sm text-[#515E58]"><span className="font-medium text-[#1F2924]">Objective: </span>{g.objective}</p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-[#515E58]">
                {g.interventions.map((it, j) => <li key={j}>{it}</li>)}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function FieldRenderer({ block, value, onChange, docs, onUploaded, readonly, error }) {
  if (block.type === "info") return <InfoBlock block={block} />;

  if (block.type === "ack") {
    return (
      <label className={`flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors ${error ? "border-[#C44F4F] bg-[#FBEDED]" : "border-[#E5E2DC] bg-white hover:bg-[#F4F2EC]"}`}>
        <Checkbox checked={!!value} onCheckedChange={(v) => onChange(!!v)} disabled={readonly}
          data-testid={`ack-${block.key}`} className="mt-0.5" />
        <span className="text-[15px] leading-relaxed text-[#1F2924]">{block.label}</span>
      </label>
    );
  }

  const { input, key } = block;

  return (
    <div className="space-y-2">
      <Label className="text-[15px] font-medium text-[#1F2924]">
        {block.label}<Asterisk show={block.required} />
      </Label>
      {block.note && <p className="text-sm italic text-[#515E58]">{block.note}</p>}

      {(input === "text" || input === "email" || input === "tel") && (
        <Input type={input} value={value || ""} placeholder={block.placeholder}
          onChange={(e) => onChange(e.target.value)} disabled={readonly} data-testid={`field-${key}`}
          className={error ? "border-[#C44F4F]" : ""} />
      )}

      {input === "textarea" && (
        <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={readonly}
          rows={block.large ? 10 : 4} data-testid={`field-${key}`}
          className={`resize-y ${error ? "border-[#C44F4F]" : ""}`} placeholder={block.placeholder} />
      )}

      {input === "select" && (
        <Select value={value || ""} onValueChange={onChange} disabled={readonly}>
          <SelectTrigger data-testid={`field-${key}`} className={error ? "border-[#C44F4F]" : ""}><SelectValue placeholder="Select…" /></SelectTrigger>
          <SelectContent>
            {block.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      )}

      {input === "radio" && (
        <RadioGroup value={value || ""} onValueChange={onChange} disabled={readonly}
          className="flex flex-wrap gap-3" data-testid={`field-${key}`}>
          {block.options.map((o) => (
            <label key={o} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-2.5 text-sm transition-colors ${value === o ? "border-[#2C5545] bg-[#EAF0ED] text-[#1F2924]" : "border-[#E5E2DC] bg-white text-[#515E58] hover:bg-[#F4F2EC]"}`}>
              <RadioGroupItem value={o} data-testid={`field-${key}-${o}`} />
              {o}
            </label>
          ))}
        </RadioGroup>
      )}

      {input === "checkboxes" && (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid={`field-${key}`}>
          {block.options.map((o) => {
            const arr = Array.isArray(value) ? value : [];
            const checked = arr.includes(o);
            return (
              <label key={o} className={`flex cursor-pointer items-center gap-2.5 rounded-lg border px-3 py-2.5 text-sm transition-colors ${checked ? "border-[#2C5545] bg-[#EAF0ED]" : "border-[#E5E2DC] bg-white hover:bg-[#F4F2EC]"}`}>
                <Checkbox checked={checked} disabled={readonly}
                  onCheckedChange={(v) => {
                    const next = v ? [...arr, o] : arr.filter((x) => x !== o);
                    onChange(next);
                  }} />
                <span className="text-[#1F2924]">{o}</span>
              </label>
            );
          })}
        </div>
      )}

      {input === "file" && <FileField block={block} docs={docs} onUploaded={onUploaded} readonly={readonly} />}

      {error && (
        <p className="text-sm text-[#C44F4F]" data-testid={`error-${key}`}>
          {input === "file"
            ? "Please upload this document to continue."
            : "This field is required. If it does not apply, enter “NA” or select the closest option."}
        </p>
      )}
    </div>
  );
}
